import { RecognitionInput, RecognitionRecipientView, RecognitionView, CompanyRecognitionValueView, CompanyRecognitionValueInput, HiddenReason } from "@/models/preformanceManagement";
import request from "../../utils/request";
import { Result } from "@/models/general";

// Dashboard-specific interfaces
export interface DashboardRecognitionView {
  id: number;
  senderId?: string;
  recipientId?: string;
  senderName: string;
  recipientName: string;
  avatarUrl?: string;
  senderImageUrl?: string;
  recipientImageUrl?: string;
  message: string;
  values: string[];
  points: number;
  date: string;
}

export interface RecognitionStatisticsView {
  receivedCount: number;
  givenCount: number;
  totalPointsReceived: number;
  totalPointsGiven: number;
  valuesBreakdown: Record<string, number>;
  monthlyTrend: Record<string, number>;
}

export interface DashboardSummaryView {
  title: string;
  value: string;
  icon: string;
  color: string;
  trend: {
    type: 'up' | 'down' | 'stable';
    value: string;
  };
  description: string;
}

export interface FiltersDto {
  dateRange?: string;
  values?: string;
  category?: string;
  type?: 'received' | 'given';
}
/* export async function getFeed(values?: string, timeRange?: string): Promise<Result<RecognitionView[]>> {
    const params: any = {};
    if (values) params.values = values;
    if (timeRange) params.timeRange = timeRange;
    const response = await request.get<Result<RecognitionView[]>>('Recognition/Feed', { params });
    return response.data;
}

export async function submitRecognition(input: RecognitionInput): Promise<Result<RecognitionView>> {
    const response = await request.post<Result<RecognitionView>>('Recognition/Submit', input);
    return response.data;
}

export async function getRecognitionUsers(): Promise<Result<RecognitionRecipientView[]>> {
    const response = await request.get<Result<RecognitionRecipientView[]>>('Recognition/Recipients');
    return response.data;
} */
export async function getFeed(values?: string, timeRange?: string): Promise<Result<RecognitionView[]>> {
  const params: any = {};
  if (values) params.values = values;
  if (timeRange) params.timeRange = timeRange;
  const response = await request.get<Result<RecognitionView[]>>('Recognition/Feed', { params });
  return response.data;
}

export async function submitRecognition(input: RecognitionInput): Promise<Result<RecognitionView>> {
  const response = await request.post<Result<RecognitionView>>('Recognition/Submit', input);
  if (!response.data) {
    throw new Error("No response data received");
  }
  return response.data;
}

export async function getRecognitionUsers(): Promise<any[]> {
  const response = await request.get('Recognition/Recipients');
  if (response.data && response.data.payload && Array.isArray(response.data.payload)) {
    return response.data.payload;
  }
  return [];
}

export async function getFullFeed(): Promise<Result<RecognitionView[]>> {
  const response = await request.get<Result<RecognitionView[]>>('Recognition/Feed');
  return response.data;
}

export async function getDashboardRecognitions(
  type: 'received' | 'given' = 'received',
  filters?: FiltersDto
  , userId?: string
): Promise<Result<DashboardRecognitionView[]>> {
  try {
    const params: any = { type };
    if (userId) params.userId = userId;

    if (filters?.dateRange) params.dateRange = filters.dateRange;
    if (filters?.values) params.values = filters.values;
    if (filters?.category) params.category = filters.category;

    const response = await request.get<Result<DashboardRecognitionView[]>>(
      'Recognition/Dashboard/Recognitions',
      { params }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getDashboardStatistics(params?: Record<string, any>): Promise<Result<RecognitionStatisticsView>> {
  try {
    const response = await request.get<Result<RecognitionStatisticsView>>(
      'Recognition/Dashboard/Statistics',
      { params }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getDashboardSummary(params?: Record<string, any>): Promise<Result<DashboardSummaryView[]>> {
  try {
    const response = await request.get<Result<DashboardSummaryView[]>>(
      'Recognition/Dashboard/Summary',
      { params }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Helper to get progress indicators (this uses statistics and calculates values)
export async function getProgressIndicators(userId: string): Promise<Array<{
  id: number;
  title: string;
  subtitle: string;
  icon: string;
  value: number;
  period: string;
  description: string;
  trend: 'up' | 'down' | 'stable';
  change: string;
}>> {
  try {
    const statsResult = await getDashboardStatistics({ userId });

    // `Result<T>` shape uses `payload`, `status`, and `errors` (see `models/general.ts`).
    if (!statsResult || typeof statsResult.status !== 'number' || statsResult.status >= 400) {
      throw new Error("Failed to fetch statistics");
    }

    const stats = statsResult.payload;

    // Transform statistics into progress indicators
    return [
      {
        id: 1,
        title: "Recognition Engagement",
        subtitle: "Received this month",
        icon: "Favorite",
        value: Math.min(100, Math.floor((stats.receivedCount / 30) * 100)), // Assuming 30 is target
        period: "This month",
        description: "Active participation",
        trend: stats.receivedCount > 0 ? "up" : "stable",
        change: "+5%"
      },
      {
        id: 2,
        title: "Value Alignment",
        subtitle: "Company values demonstrated",
        icon: "Star",
        value: Object.keys(stats.valuesBreakdown).length > 0 ? 75 : 40,
        period: "This quarter",
        description: "Core values engagement",
        trend: "up",
        change: "+12%"
      },
      {
        id: 3,
        title: "Recognition Impact",
        subtitle: "Points influence",
        icon: "TrendingUp",
        value: Math.min(100, Math.floor((stats.totalPointsReceived / 500) * 100)), // Assuming 500 is max
        period: "This week",
        description: "Team contribution level",
        trend: "up",
        change: "+8%"
      }
    ];
  } catch {
    return []; // Return empty array on error
  }
}

export async function getAchievements(): Promise<Result<Array<{
  id: number;
  title: string;
  description: string;
  icon: string;
  category: string;
  earnedDate: string;
  rarity: number;
  isNew: boolean;
}>>> {
  try {
    // For now, return mock achievements or call your achievement endpoint
    const mockAchievements = [
      {
        id: 1,
        title: "Recognition Champion",
        description: "Received 20+ recognitions from colleagues across different teams and departments.",
        icon: "Heart",
        category: "Recognition",
        earnedDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        rarity: 15,
        isNew: true
      },
      {
        id: 2,
        title: "Team Player",
        description: "Consistently collaborated across teams and helped colleagues achieve their goals.",
        icon: "Users",
        category: "Collaboration",
        earnedDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        rarity: 25,
        isNew: false
      }
    ];

    return {
      payload: mockAchievements,
      status: 200,
      errors: []
    };
  } catch (error) {
    throw error;
  }
}

export async function getRecognitionGivenAchievement(): Promise<Result<boolean>> {
  try {
    const response = await request.get<Result<boolean>>(
      'Recognition/Achievements/Given'
    );

    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getRecognitionReceivedAchievement(): Promise<Result<boolean>> {
  try {
    const response = await request.get<Result<boolean>>(
      'Recognition/Achievements/Received'
    );

    return response.data;
  } catch (error) {
    throw error;
  }
}

// Company Recognition Values API Calls
export async function getCompanyRecognitionValues(): Promise<Result<CompanyRecognitionValueView[]>> {
  try {
    const response = await request.get<Result<CompanyRecognitionValueView[]>>(
      'Recognition/recognition-values'
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function addCompanyRecognitionValue(input: CompanyRecognitionValueInput): Promise<Result<CompanyRecognitionValueView>> {
  try {
    const response = await request.post<Result<CompanyRecognitionValueView>>(
      'Recognition/recognition-values',
      input
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateCompanyRecognitionValue(id: number, input: CompanyRecognitionValueInput): Promise<Result<CompanyRecognitionValueView>> {
  try {
    const response = await request.put<Result<CompanyRecognitionValueView>>(
      `Recognition/recognition-values/${id}`,
      input
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteCompanyRecognitionValue(id: number): Promise<Result<boolean>> {
  try {
    const response = await request.delete<Result<boolean>>(
      `Recognition/recognition-values/${id}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Mirrors the backend RecognitionRecencyBand enum. .NET enums serialize as the member NAME.
export enum RecognitionRecencyBand {
  Never = "Never",
  OverThirty = "OverThirty",
  WithinThirty = "WithinThirty",
  WithinTwentyOne = "WithinTwentyOne",
  WithinFourteen = "WithinFourteen",
  WithinSeven = "WithinSeven",
}

// Deliberately carries no date and no day count — the server never sends one.
export interface RecognitionGapRowView {
  employeeId: string;
  employeeName?: string;
  departmentName?: string;
  band: RecognitionRecencyBand;
}

// One shape for every grouping the tab shows. An ABSENT name is the "no department"/"no branch"
// bucket: the API omits null properties (Newtonsoft NullValueHandling.Ignore), so test with ?? or
// a falsy check, never against null.
export interface RecognitionGroupStatView {
  name?: string;
  recognizedCount: number;
  notRecognizedCount: number;
  score: number;
  /** CO-915: fewer than 5 people in the group; score and counts are 0 and must not be shown. */
  hidden?: boolean;
  /** CO-932 follow-up: 'floor' = under 5 people; 'subtraction' = hidden so a smaller hidden group
      cannot be worked out (5+ people). Absent when shown, and on older payloads (treat as 'floor'). */
  hiddenReason?: HiddenReason;
}

// companyScore always equals the dashboard Q4; filteredScore is the headline the tab renders.
export interface RecognitionScoreBreakdownView {
  companyScore: number;
  filteredScore: number;
  departments: RecognitionGroupStatView[];
  branches: RecognitionGroupStatView[];
  // Absent, not null, for company-wide callers. Existing `!= null` checks still work.
  teamScore?: number;
  teamDepartments: RecognitionGroupStatView[];
}

export interface RecognitionGapView {
  rows: RecognitionGapRowView[];
  neverCount: number;
  overThirtyCount: number;
  withinThirtyCount: number;
  withinTwentyOneCount: number;
  withinFourteenCount: number;
  withinSevenCount: number;
  scoreBreakdown?: RecognitionScoreBreakdownView;
  // True when the caller has at least one direct report. Lets the UI tell
  // "your reports are all recognized" apart from "you have no reports recorded".
  hasDirectReports: boolean;
  // True when the caller could opt into a wider, department-level view.
  hasDepartmentPeers: boolean;
  // True for HR/Admin/Chief, whose list is the whole company rather than a team.
  isCompanyScope: boolean;
  // The caller's own department name when not company-scoped; labels the filtered card.
  scopeDepartmentName?: string;
}

// Five coarse display buckets. WithinTwentyOne + WithinThirty fold into "≤30"; carries no date.
export type DisplayBand = 'never' | 'le7' | 'le15' | 'le30' | 'gt30';

export const toDisplayBand = (b: RecognitionRecencyBand): DisplayBand => {
  switch (b) {
    case RecognitionRecencyBand.Never:
      return 'never';
    case RecognitionRecencyBand.WithinSeven:
      return 'le7';
    case RecognitionRecencyBand.WithinFourteen:
      return 'le15';
    case RecognitionRecencyBand.WithinTwentyOne:
    case RecognitionRecencyBand.WithinThirty:
      return 'le30';
    case RecognitionRecencyBand.OverThirty:
    default:
      return 'gt30';
  }
};

export interface RecognitionReasonItemView {
  title: string;
  detail: string;
}

export interface RecognitionReasonView {
  summary: string;
  reasons: RecognitionReasonItemView[];
  aiAvailable: boolean;
  // True when the employee has no completed goals — shown as a neutral note, not the AI-off banner.
  noGoals?: boolean;
}

// AI-drafted recognition message for the composer. The human always edits and sends; nothing auto-sends.
export interface RecognitionDraftView {
  message: string;
  suggestedValues: string[];
  aiAvailable: boolean;
  noGoals: boolean;
}

// Employees who are overdue for recognition, within the caller's server-derived scope.
export async function getRecognitionGaps(
  departmentId?: number,
  branchId?: number,
  includeDepartment?: boolean
): Promise<Result<RecognitionGapView>> {
  const params: Record<string, number | boolean> = {};
  if (departmentId !== undefined) params.departmentId = departmentId;
  if (branchId !== undefined) params.branchId = branchId;
  if (includeDepartment) params.includeDepartment = true;
  const response = await request.get<Result<RecognitionGapView>>('Recognition/Gaps', { params });
  return response.data;
}

// AI advisory: what could this person be recognized for. Never drafts the message.
export async function getRecognitionReasons(
  employeeId: string
): Promise<Result<RecognitionReasonView>> {
  const response = await request.post<Result<RecognitionReasonView>>(
    'RecognitionAi/RecognitionReasons',
    { employeeId }
  );
  return response.data;
}

// AI draft of a recognition message for this person, pre-filled into the composer for the human to edit and send.
export async function draftRecognition(
  employeeId: string
): Promise<Result<RecognitionDraftView>> {
  const response = await request.post<Result<RecognitionDraftView>>(
    'RecognitionAi/DraftRecognition',
    { employeeId }
  );
  return response.data;
}
